export interface InboxV2ApiCacheOptions {
  cacheKey: string;
  expireMs?: number;
  forceNoCache?: boolean;
}

interface InboxV2ApiCacheEntry<Data> {
  data: Data;
  expiresAt: number;
}

const cachedResponses = new Map<string, InboxV2ApiCacheEntry<unknown>>();
const pendingResponses = new Map<string, Promise<unknown>>();
const cacheVersions = new Map<string, number>();

export function getInboxV2CachedApiResponse<Data>(
  options: InboxV2ApiCacheOptions,
  load: () => Promise<Data>
): Promise<Data> {
  const cachedResponse = readInboxV2ApiCache<Data>(options.cacheKey);

  if (!options.forceNoCache && cachedResponse !== undefined) {
    return Promise.resolve(cachedResponse);
  }

  const pendingResponse = pendingResponses.get(options.cacheKey);

  if (!options.forceNoCache && pendingResponse) {
    return pendingResponse as Promise<Data>;
  }

  const requestVersion = getInboxV2ApiCacheVersion(options.cacheKey);
  const request = load()
    .then((data) => {
      if (getInboxV2ApiCacheVersion(options.cacheKey) === requestVersion) {
        writeInboxV2ApiCache(options, data);
      }

      return data;
    })
    .finally(() => {
      if (pendingResponses.get(options.cacheKey) === request) {
        pendingResponses.delete(options.cacheKey);
      }
    });

  pendingResponses.set(options.cacheKey, request);

  return request;
}

export function readInboxV2ApiCache<Data>(cacheKey: string): Data | undefined {
  const cachedResponse = cachedResponses.get(cacheKey);

  if (!cachedResponse) {
    return undefined;
  }

  if (cachedResponse.expiresAt <= Date.now()) {
    cachedResponses.delete(cacheKey);

    return undefined;
  }

  return cachedResponse.data as Data;
}

export function writeInboxV2ApiCache<Data>(
  options: InboxV2ApiCacheOptions,
  data: Data
) {
  cachedResponses.set(options.cacheKey, {
    data,
    expiresAt: Date.now() + (options.expireMs ?? 0),
  });
}

export function invalidateInboxV2ApiCache(cacheKey: string) {
  cachedResponses.delete(cacheKey);
  pendingResponses.delete(cacheKey);
  cacheVersions.set(cacheKey, getInboxV2ApiCacheVersion(cacheKey) + 1);
}

export function invalidateInboxV2ApiCacheByPrefix(cacheKeyPrefix: string) {
  const matchingKeys = new Set([
    ...cachedResponses.keys(),
    ...pendingResponses.keys(),
    ...cacheVersions.keys(),
  ]);

  for (const cacheKey of matchingKeys) {
    if (
      cacheKey === cacheKeyPrefix ||
      cacheKey.startsWith(`${cacheKeyPrefix}:`)
    ) {
      invalidateInboxV2ApiCache(cacheKey);
    }
  }
}

export function resetInboxV2ApiCacheForTests() {
  cachedResponses.clear();
  pendingResponses.clear();
  cacheVersions.clear();
}

function getInboxV2ApiCacheVersion(cacheKey: string) {
  return cacheVersions.get(cacheKey) ?? 0;
}
